import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { DollarSign, TrendingUp, TrendingDown, Package, Users, AlertCircle } from 'lucide-react';
import { LoadingState } from '../ui';
import useAnalyticsStore from '../../stores/analyticsStore';
import { getCostOverview, getCostComparison } from '../../services/analyticsService';

const CostAnalysisTab = () => {
  const { dateRange } = useAnalyticsStore();

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['cost-overview', dateRange],
    queryFn: () => getCostOverview({ days: dateRange }),
    staleTime: 60000
  });

  const { data: comparison, isLoading: comparisonLoading } = useQuery({
    queryKey: ['cost-comparison', dateRange],
    queryFn: () => getCostComparison({ days: dateRange }),
    staleTime: 60000
  });

  if (overviewLoading || comparisonLoading) {
    return <LoadingState message="Loading cost analytics..." />;
  }

  if (!overview || !overview.summary) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No cost data available for the selected period</p>
      </div>
    );
  }

  const { summary, by_supplier, by_material, daily_trend } = overview;

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Prepare chart data
  const supplierData = (by_supplier || []).slice(0, 10).map(s => ({
    name: s.supplier_code,
    cost: s.estimated_cost,
    quantity: s.total_quantity
  }));

  const materialData = (by_material || []).slice(0, 10).map(m => ({
    name: m.material_code,
    cost: m.estimated_cost,
    quantity: m.total_quantity
  }));

  const trendData = (daily_trend || []).map(d => ({
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    cost: d.estimated_cost,
    receipts: d.receipts
  }));

  // Pie chart colors
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

  // Calculate change indicators
  const receiptChange = comparison?.changes?.receipts_change_pct || 0;
  const qtyChange = comparison?.changes?.quantity_change_pct || 0;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard
          title="Total Estimated Cost"
          value={formatCurrency(summary.total_estimated_cost || 0)}
          icon={DollarSign}
          color="blue"
        />
        <KPICard
          title="Avg Cost/Day"
          value={formatCurrency(summary.avg_cost_per_day || 0)}
          icon={TrendingUp}
          color="green"
        />
        <KPICard
          title="Total Receipts"
          value={summary.total_receipts || 0}
          icon={Package}
          color="purple"
          trend={receiptChange}
        />
        <KPICard
          title="Avg Cost/Receipt"
          value={formatCurrency(summary.avg_cost_per_receipt || 0)}
          icon={Users}
          color="orange"
        />
      </div>

      {/* Period Comparison */}
      {comparison && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Period Comparison</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Current Period Receipts</p>
              <p className="text-2xl font-bold text-gray-900">{comparison.current_period?.receipts || 0}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Previous Period Receipts</p>
              <p className="text-2xl font-bold text-gray-900">{comparison.previous_period?.receipts || 0}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Change</p>
              <div className={`flex items-center justify-center text-2xl font-bold ${receiptChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {receiptChange >= 0 ? <TrendingUp className="h-6 w-6 mr-2" /> : <TrendingDown className="h-6 w-6 mr-2" />}
                {Math.abs(receiptChange).toFixed(1)}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Daily Cost Trend */}
      {trendData.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Cost Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip formatter={(value, name) => [name === 'cost' ? formatCurrency(value) : value, name === 'cost' ? 'Estimated Cost' : 'Receipts']} />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="cost" stroke="#3B82F6" strokeWidth={2} name="Estimated Cost" />
              <Line yAxisId="right" type="monotone" dataKey="receipts" stroke="#10B981" strokeWidth={2} strokeDasharray="5 5" name="Receipts" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Cost by Supplier & Material */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost by Supplier */}
        {supplierData.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost by Supplier (Top 10)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={supplierData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" width={80} fontSize={11} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="cost" fill="#3B82F6" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Cost by Material */}
        {materialData.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost by Material (Top 10)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={materialData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" width={80} fontSize={11} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="cost" fill="#10B981" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Cost Breakdown Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Supplier Cost Breakdown</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Receipts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estimated Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  % of Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(by_supplier || []).slice(0, 15).map((supplier, index) => {
                const pctOfTotal = summary.total_estimated_cost > 0
                  ? (supplier.estimated_cost / summary.total_estimated_cost * 100).toFixed(1)
                  : 0;
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {supplier.supplier_code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {supplier.receipts}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {supplier.total_quantity.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(supplier.estimated_cost)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${Math.min(pctOfTotal, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">{pctOfTotal}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// KPI Card Component
const KPICard = ({ title, value, icon: Icon, color, trend }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {trend !== undefined && trend !== 0 && (
        <div className={`flex items-center mt-1 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
          {Math.abs(trend).toFixed(1)}% vs prev period
        </div>
      )}
    </div>
  );
};

export default CostAnalysisTab;
